/**
 * Distributing many photos across many slots, in one gesture.
 *
 * A 30-photo collage die-hards a form of 30 upload fields, and nobody fills 30
 * fields one at a time — they select 30 files at once and expect the product to
 * fill itself. The failure mode this prevents is losing exactly the customer
 * who wanted the biggest product: the one with the most photos and the least
 * patience.
 *
 * The planning is separated from the DOM because the rules are worth pinning:
 * which slots receive files, in what order, and what happens when there are
 * more files than slots.
 */

export interface SpillSlot {
  id: string;
  /** Only photo-upload fields take part; everything else is skipped. */
  isPhoto: boolean;
  /** A slot already holding a photo is never silently overwritten. */
  answered: boolean;
  /** Hidden by a condition ("5 slika" not picked): not on offer right now. */
  hidden: boolean;
}

/**
 * Which slot receives each file.
 *
 * The order is the shopper's mental model: the slot they dropped onto comes
 * first — replacing its content if it had any, because dropping ONTO a filled
 * slot is an explicit request — then empty slots after it in form order, then
 * empty slots before it, wrapping around. Filled slots other than the target
 * are never touched: photo 7 of 30 must not eat photo 3 because the shopper
 * re-dropped one file.
 */
export function planPhotoSpill(
  slots: SpillSlot[],
  startId: string,
  fileCount: number,
): string[] {
  if (fileCount <= 0) return [];
  const start = slots.findIndex((s) => s.id === startId);
  if (start < 0) return [];

  const targets: string[] = [slots[start]!.id];

  // Empty, visible photo slots after the start, then before it.
  const following = slots.slice(start + 1);
  const preceding = slots.slice(0, start);
  for (const slot of [...following, ...preceding]) {
    if (targets.length >= fileCount) break;
    if (!slot.isPhoto || slot.answered || slot.hidden) continue;
    targets.push(slot.id);
  }
  return targets.slice(0, fileCount);
}

/**
 * Runs jobs a few at a time.
 *
 * Thirty uploads fired at once from a phone stall each other and trip rate
 * limits; strictly one at a time makes the shopper watch a progress bar for
 * minutes. A small window keeps the pipe full without flooding it. Failures
 * don't stop the rest — each slot reports its own.
 */
export async function runLimited<T>(
  jobs: readonly T[],
  limit: number,
  run: (job: T) => Promise<void>,
): Promise<void> {
  const queue = [...jobs];
  const workers = Array.from({ length: Math.max(1, Math.min(limit, queue.length)) }, async () => {
    for (;;) {
      const job = queue.shift();
      if (job === undefined) return;
      try {
        await run(job);
      } catch {
        // The per-slot UI already showed the failure; the batch continues.
      }
    }
  });
  await Promise.all(workers);
}
